import React, { useCallback, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import {
  buildingEntity,
  employeeEntity,
  tenantEntity,
  ticketTagEntity,
} from 'shared/entityDetails';
import {
  NumberEditor,
  TextAreaEditor,
  TextEditor,
  withStaticSelectEditor,
} from 'shared/components/data-display/editors';
import { getTenantsInUnit, searchBuildingUnits } from 'shared/api/units';
import {
  AsyncMultipleSelect,
  AsyncSearchableSelect,
} from 'shared/components/select/SearchableSelects';
import { TICKET_PRIORITY } from '../../../shared/constants/support';

/**
 * @param {Object} props
 * @param {Object} props.state
 * @param {Array<Object>} props.stagedTicketTags
 * @param {Function} props.handleTextChange
 * @param {Function} props.handleTicketContactChange
 * @param {Function} props.setAssignee
 * @param {Function} props.setBuilding
 * @param {Function} props.setMinutes
 * @param {Function} props.setUnit
 * @param {Function} props.setTenant
 * @param {Function} props.setTicketTags
 * @param {Function} props.submitTicket
 * @param {*} props.notification
 */
export default function TicketForm(props) {
  const {
    state: {
      assignee,
      building,
      unit,
      tenant,
      text,
      priority,
      title,
      minutes,
      ticketContact,
    },
    stagedTicketTags,
    handleTextChange,
    handleTicketContactChange,
    setAssignee,
    setBuilding,
    setMinutes,
    setTenant,
    setTicketTags,
    setUnit,
    notification,
  } = props;

  const [disableOptions, setDisableOptions] = useState(false);

  useEffect(() => {
    if (stagedTicketTags.length > 4) {
      setDisableOptions(true);
    } else {
      setDisableOptions(false);
    }
  }, [stagedTicketTags.length]);

  const searchUnits = useCallback(
    (searchString) => searchBuildingUnits(
      searchString,
      '',
      building?.id,
    ),
    [building],
  );
  const searchTenants = useCallback(
    (searchString) => getTenantsInUnit(unit?.id, searchString),
    [unit],
  );

  const validateTagLength = () => {
    if (!stagedTicketTags || stagedTicketTags.length > 5 || stagedTicketTags.length === 0) {
      notification.pushError('Must select between 1 - 5 tags');
    }
  };

  return (
    <>
      <Grid size={{
        xs: 12,
        xl: 8,
      }}
      >
        <div className="item-child-title">Basic Information</div>
        <Grid
          container
          columnSpacing={2}
          rowSpacing={1.5}
          sx={{ '& > .MuiGrid-root': { display: 'flex', flexDirection: 'column', rowGap: 1 } }}
        >
          <Grid size={{
            xs: 12,
            xl: 6,
          }}
          >
            <AsyncSearchableSelect
              baseSearch="?expand=units"
              defaultOptions={false}
              label="Building"
              onChange={setBuilding}
              searchRequest={buildingEntity.searchRequest}
              selectedOption={building}
            />
            <AsyncSearchableSelect
              onChange={setUnit}
              label="Unit"
              searchRequest={searchUnits}
              disabled={building?.id === undefined}
              selectedOption={unit}
            />
            <AsyncSearchableSelect
              baseSearch="include_archived=true&ob=it_firstname"
              getOptionLabel={tenantEntity.getOptionLabel}
              label="Tenant"
              onChange={setTenant}
              searchRequest={searchTenants}
              disabled={unit?.id === undefined}
              selectedOption={tenant}
            />
            <TextEditor label="Title" value={title} setValue={handleTextChange('title')} />
          </Grid>
          <Grid size={{
            xs: 12,
            xl: 6,
          }}
          >
            {withStaticSelectEditor(Object.values(TICKET_PRIORITY))({
              label: 'Priority',
              setValue: handleTextChange('priority'),
              value: priority,
              fullWidth: true,
            })}
            <AsyncMultipleSelect
              searchRequest={ticketTagEntity.searchRequest}
              getOptionLabel={ticketTagEntity.getOptionLabel}
              label="Ticket Tag"
              onChange={setTicketTags}
              selectedOptions={stagedTicketTags}
              onBlur={validateTagLength}
              getOptionDisabled={() => disableOptions}
            />
            <AsyncSearchableSelect
              getOptionLabel={employeeEntity.getOptionLabel}
              getOptionValue={employeeEntity.getOptionValue}
              label="Assignee"
              onChange={setAssignee}
              searchRequest={employeeEntity.searchRequest}
              selectedOption={assignee}
            />
            <NumberEditor
              label="Minutes"
              setValue={setMinutes}
              value={minutes}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid size={{
        xs: 12,
        xl: 4,
      }}
      >
        <div className="item-child-title">Contact Information</div>
        <Grid sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          rowGap: 1,
        }}
        >
          <TextEditor
            setValue={handleTicketContactChange('name')}
            label="Name"
            value={ticketContact.name}
          />
          <TextEditor
            setValue={handleTicketContactChange('phone')}
            label="Phone"
            value={ticketContact.phone}
          />
          <TextEditor
            setValue={handleTicketContactChange('email')}
            label="Email"
            value={ticketContact.email}
          />
        </Grid>
      </Grid>
      <Grid size={{
        xl: 8,
        xs: 12,
      }}
      >
        <TextAreaEditor label="Description" setValue={handleTextChange('text')} value={text} />
      </Grid>
    </>
  );
}
